/**
 * Glue layer between game state, Rive layers, Pixi chip animations, and the
 * minimal DOM status text. Mirrors the imperative side-effect orchestration
 * in `_GameScreenState` of
 *   packages/amgbaccarat_v2/lib/presentation/screens/game_screen.dart
 *   + the multiplayer half of game_bloc.dart.
 *
 * Baccarat differs from Dragon Tiger in a few crucial ways:
 *   - Up to 3 cards per side; reveal sequence is P1 → B1 → P2 → B2 →
 *     (P3 if drawn) → (B3 if drawn), 1 s between steps (game_bloc.dart:1193-1228).
 *   - 7 bet IDs (player/banker/tie/playerPair/bankerPair/perfectPair/eitherPair).
 *   - No unified BetPanel — separate triggers for StartBet / NoMoreBet /
 *     winner (player/banker/tie).
 *   - Result-sequence timings (game_bloc.dart:1262-1346):
 *       t=2 s — chip-fly-out based on winMode + history append
 *       t=5 s — win/lose popup (single-player)
 *       t=8 s — clearCard(getClearCardType(playerCount, bankerCount))
 *       t=13 s — StartNewRound
 */
import type { BetType, GamePhase, GameResult, GameRound } from '../types/game';
import {
  BET_ITEM_ID,
  betTypeFromId,
  clearCardType,
  isPair,
} from '../types/game';
import {
  DEFAULT_AGENT_ID,
  DEFAULT_GAME_CONFIG,
  DEFAULT_PASSWORD,
  GAME_KEY_MULTI,
  GAME_KEY_SINGLE,
} from '../config';
import type { Api } from '../net/api';
import { ApiError } from '../net/api';
import type { GameState, GameStateSnapshot } from './gameState';
import type { RiveLayer } from '../rive/riveLayer';
import type { ChipLayer } from '../pixi/chipLayer';
import type { Hud } from '../ui/hud';
import type { TableControls } from '../ui/controls';
import type { UrlParams } from './urlParams';
import type { MultiplayerHub, HubEvent } from '../net/multiplayerHub';
// showModeSelectionDialog disabled — the mode is now derived from the launch
// URL (the `g` game key + path) the lobby routes to. Kept importable from
// '../ui/modeSelectionDialog' if the dialog is ever re-enabled.
import { type GameMode } from '../ui/modeSelectionDialog';
import type { GameTimerWidget } from '../ui/timerWidget';
import { cardCount, parseResultString } from './resultParser';
import { GameLogManager } from './gameLogManager';
import { MatchHistoryManager } from './matchHistoryManager';
import { debugLog, error, warn } from '../util/logger';

/**
 * Multiplayer only: end the visible bet countdown this many seconds before
 * the server's real betting-window close. The batched bet submission fires
 * when the countdown reaches 0, so this buffer gives it time to reach the
 * backend before betting closes and the round starts playing.
 */
const BET_SUBMIT_BUFFER_SEC = 2;

interface OrchestratorDeps {
  state: GameState;
  api: Api;
  rive: RiveLayer;
  chips: ChipLayer;
  hud: Hud;
  controls: TableControls;
  hub: MultiplayerHub;
  timer: GameTimerWidget;
  params: UrlParams;
  onInteractive?: () => void;
}

export class GameOrchestrator {
  private readonly d: OrchestratorDeps;
  private gid: string = '';

  // Result-sequence guard flags — reset every round.
  private didStartCardReveal = false;
  private didStartResultSequence = false;
  private didShowWinLose = false;
  // The YOU WIN / YOU LOSE reveal point (t=5 s) has been reached. Lets a late
  // multiplayer Result — which patches in the real win/lose AFTER the reveal ran
  // with a still-zero amount — drive the popup as a backstop (see handleResult).
  private endingRevealDone = false;
  private didResetAfterResult = false;
  // Balance settlement is deferred to the WIN/LOSE reveal (t=5 s) so the
  // displayed (available) balance stays "bet deducted" through the card
  // reveal and only reflects winnings once the result is shown.
  private pendingSettleBalance: number | null = null;
  private didSettle = false;
  // Multiplayer: the result reveal reached the settle point but the server's
  // Result event (which carries the post-round balance) hasn't arrived yet.
  // We hold the bets in place until it does (see applySettlement) so the
  // displayed available balance never flashes the pre-settlement wallet.
  private awaitingSettleBalance = false;
  // Bumped at every round start; settlement captures it before the async balance
  // fetch and bails if a new round started meanwhile (stale fetch must not settle
  // into the next round's wallet).
  private roundToken = 0;
  // Guards applyResultSettlement to once per round (a retransmitted settlement
  // Result must not recompute against the already-settled wallet / double-log).
  private didApplyResultSettlement = false;
  // Multiplayer bets are NOT sent per-chip; they're batched and submitted
  // when the bet countdown ends, so the player can still clear beforehand.
  private didSubmitMpBets = false;
  private sequenceTimers: number[] = [];

  /**
   * Monotonic round token. Bumped on every round (re)start — startBettingRound
   * (single-player) and handleStartGame (multiplayer) — AND when
   * resyncToCurrentState() takes over driving the visuals on tab-return. The
   * card-reveal sequence captures it and bails after each inter-card wait if it
   * changed, so an in-flight reveal can never fire card triggers into a stale or
   * freshly-started round (or fight the resync snap).
   */
  private revealEpoch = 0;

  // ---- Tab-visibility "stay live, snap on return" bookkeeping ----
  // Browsers freeze Rive's rAF for hidden tabs, so any Rive trigger fired while
  // hidden collapses (consumed once on the single resume-advance). The round
  // timeline keeps running on wall-clock so it never lags the server; on return
  // resyncToCurrentState() re-drives Rive from the authoritative state. These
  // flags let it know what was (or wasn't) actually shown so it can repair it.
  //
  // Whether the dealt cards are currently drawn on the table. Set when a reveal
  // deals them; cleared when clearCard runs WHILE VISIBLE (so its animation
  // actually played). If clearCard fired while hidden it collapsed, so this
  // stays true and resync re-issues the clear on the live SM.
  private cardsVisible = false;
  // Whether the t=8 s clearCard has run for this round, and the type it used —
  // so resync can flush stale card faces even after currentRound is gone.
  private didClearCard = false;
  private lastClearCardType: 1 | 2 | 3 | 4 | null = null;
  // Whether the StartBet / NoMoreBet popups were actually shown to a VISIBLE
  // tab this round (set false when fired into a hidden/frozen SM, so resync
  // re-fires them on return). Reset each round.
  private didShowStartBetPopup = false;
  private didShowNoMoreBet = false;
  // Whether this round was recorded in the bead-plate history yet. The add
  // normally happens in playCardRevealSequence, but in multiplayer that runs
  // behind a 1 s wait that a tab-return resync can orphan — so snapBoard records
  // it too, guarded by this flag so the round is logged exactly once. Reset each round.
  private didRecordHistory = false;
  // Bumped at the start of every resync so an older resync's micro-stagger rAF
  // loop aborts when the tab is toggled again quickly.
  private resyncToken = 0;

  // Multiplayer-specific timers.
  private betCountdownHandle: number | null = null;
  private restartCountdownHandle: number | null = null;
  private lastMpEventIndex: number | null = null;

  constructor(deps: OrchestratorDeps) {
    this.d = deps;
  }

  async start(): Promise<void> {
    this.d.state.on((next, prev) => this.onStateChange(next, prev));
    this.bindRiveEvents();
    this.bindHubEvents();
    this.d.controls.bindChips(this.d.state.config);
    this.d.controls.setSelectedChip(this.d.state.selectedChipIndex);
    this.d.controls.setControlsEnabled(false, false);

    this.d.hud.setUserName(this.d.params.userName);
    this.d.state.setPhase('initializing');
    this.d.hud.setStatus('Connecting…');

    try {
      await this.signIn();
      await this.loadConfigAndBalance();
      this.d.onInteractive?.();

      // Mode selection dialog disabled — the mode is now derived from the
      // launch URL the lobby routes to (single `/?g=BKA1` vs multi
      // `/baccarathub?g=BKAM`; see urlParams.resolveGameMode) instead of
      // prompting the player at launch. Original dialog code kept below
      // (commented) for reference.
      //
      // this.d.state.setPhase('modeSelection');
      // this.d.hud.setStatus('Select mode');
      // const mode: GameMode = await showModeSelectionDialog();
      // debugLog('[Orchestrator] mode selected:', mode);
      const mode: GameMode = this.d.params.gameMode;
      debugLog('[Orchestrator] mode from launch URL (g/path):', mode);

      if (mode === 'multi') {
        await this.startMultiplayer();
      } else {
        this.startBettingRound();
      }
    } catch (e) {
      error('[Orchestrator] start failed:', e);
      this.d.state.setError(extractMessage(e));
      this.d.hud.setStatus('ERROR: ' + extractMessage(e));
    }
  }

  // =================================================================
  // Bootstrap
  // =================================================================

  private async signIn(): Promise<void> {
    const { params } = this.d;
    const agentId = params.agentId || DEFAULT_AGENT_ID;

    let bearerToken: string;
    let gsid: string;

    if (params.token && params.gsid) {
      debugLog('[Orchestrator] Using token+gsid from URL params (no login)');
      bearerToken = ensureUrlEncoded(params.token);
      gsid = params.gsid;
    } else {
      this.d.hud.setStatus('Logging in…');
      const login = await this.d.api.login({
        userName: params.userName,
        agentId,
        password: DEFAULT_PASSWORD,
      });
      if (!login.sessionId) throw new Error('Login response missing SessionId');

      this.d.api.setCredentials({
        sessionToken: login.sessionId,
        gameSessionId: null,
        agentId: login.agentId || agentId,
        userName: params.userName,
      });

      this.d.hud.setStatus('Opening game…');
      const session = await this.d.api.openGame({
        userName: params.userName,
        agentId: login.agentId || agentId,
        gameKey: GAME_KEY_SINGLE,
      });
      debugLog('[Orchestrator] openGame OK, currentGameUrl=', session.currentGameUrl);

      const parsed = parseGameUrl(session.currentGameUrl, login.sessionId);
      bearerToken = ensureUrlEncoded(parsed.gameToken);
      gsid = parsed.gsid || session.sessionId;

      this.d.state.setBalance(session.balance);
      this.applyServerConfig(session.chips, session.minBet, session.maxBet);
    }

    this.d.api.setCredentials({
      sessionToken: bearerToken,
      gameSessionId: gsid,
      agentId,
      userName: params.userName,
    });
    this.gid = gsid;

    this.d.hud.setStatus('Fetching balance…');
    try {
      const balance = await this.d.api.checkBalance({ sessionId: gsid });
      this.d.state.setBalance(balance);
    } catch (e) {
      warn('[Orchestrator] checkBalance failed (continuing):', e);
    }
  }

  private async loadConfigAndBalance(): Promise<void> {
    const sessionId = this.d.api.getCredentials().gameSessionId ?? '';
    try {
      const info = await this.d.api.getInfo({ sessionId });
      if (info.balance > 0) this.d.state.setBalance(info.balance);
      this.applyServerConfig(info.chips, info.minBet, info.maxBet);
    } catch (e) {
      warn('[Orchestrator] getInfo failed (continuing with defaults):', e);
      if (this.d.state.config.chipValues.length === 0) {
        this.d.state.setConfig({
          minBet: DEFAULT_GAME_CONFIG.minBet,
          maxBet: DEFAULT_GAME_CONFIG.maxBet,
          chipValues: [...DEFAULT_GAME_CONFIG.chipValues],
        });
      }
    }
  }

  private applyServerConfig(chips: number[], minBet: number, maxBet: number): void {
    const current = this.d.state.config;
    const nextChips = chips.length > 0 ? chips : [...current.chipValues];
    const nextMin = minBet > 0 ? minBet : current.minBet;
    const nextMax = maxBet > 0 ? maxBet : current.maxBet;
    debugLog(
      '[Orchestrator] applyServerConfig chips=', nextChips,
      'min=', nextMin, 'max=', nextMax,
    );
    this.d.state.setConfig({
      minBet: nextMin,
      maxBet: nextMax,
      chipValues: nextChips,
    });
  }

  // =================================================================
  // Rive event bindings
  // =================================================================

  private bindRiveEvents(): void {
    this.d.rive.on('placeBetClick', (payload) => {
      const id = (payload.betID ?? payload.betId ?? payload.id) as number | undefined;
      if (typeof id !== 'number') return;
      const bet = betTypeFromId(id);
      if (bet) this.tryPlaceBet(bet);
    });

    this.d.rive.on('evtClickChips', (payload) => {
      const i = (payload.chipsAmt ?? payload.chipIndex ?? payload.index) as number | undefined;
      if (typeof i === 'number') this.d.state.selectChip(i);
    });

    this.d.rive.on('evtClearClick', () => this.clearAll());
    this.d.rive.on('evtButtonPlayClick', () => void this.confirm());

    this.d.rive.on('UIButtonClick', (payload) => {
      const parts = String(payload.parts ?? '');
      if (parts === 'startBetEnd') {
        this.d.rive.triggerGirlAnim(1);
      } else if (parts === 'noMoreBet_End') {
        if (this.didStartCardReveal) return;
        const round = this.d.state.currentRound;
        if (!round) return;
        this.didStartCardReveal = true;
        void this.playCardRevealSequence(round);
      }
    });

    this.d.rive.on('evtResultStop', () => {
      this.d.rive.triggerGirlAnim(2);
      this.startResultSequence();
    });

    this.d.rive.on('evtYouWinEnd', () => this.resetToBettingIfNeeded());
    this.d.rive.on('evtYouLoseEnd', () => this.resetToBettingIfNeeded());
  }

  // =================================================================
  // Player actions
  // =================================================================

  tryPlaceBet(bet: BetType): void {
    const s = this.d.state;
    if (s.phase !== 'betting') return;
    if (s.isMultiplayer && !s.multiplayerCanBet) return;

    const chip = s.selectedChipValue;
    const chipIndex = s.selectedChipIndex;
    const ok = s.placeBet(bet, chip);
    if (!ok) {
      debugLog('[Orchestrator] placeBet rejected', bet, chip);
      return;
    }
    // Visual chip flies in immediately and the available balance drops at
    // once (req: balance reflects the bet immediately). Bets are NOT sent to
    // the backend here — single-player submits on Play (confirm) and
    // multiplayer submits when the bet countdown ends — so they stay locally
    // clearable until then (req: player can still clear the bets).
    this.d.chips.placeChip(bet, chip, chipIndex);
    this.d.rive.highlightBetArea(BET_ITEM_ID[bet]);
  }

  clearAll(): void {
    const s = this.d.state;
    if (s.phase !== 'betting') return;
    // In multiplayer, once the countdown ends we auto-submit and lock the
    // bets — disallow clearing past that point.
    if (s.isMultiplayer && !s.multiplayerCanBet) return;
    s.clearBets();
    this.d.chips.hardClear();
  }

  async confirm(): Promise<void> {
    if (this.d.state.phase !== 'betting') return;
    if (this.d.state.isMultiplayer) {
      debugLog('[Orchestrator] confirm() ignored in multiplayer mode');
      return;
    }

    const bets = this.d.state.bets.toBetLines();
    if (bets.length === 0) return;

    this.d.state.setPhase('bettingClosed');
    this.d.state.setPhase('waitingResult');

    try {
      const sid = this.d.api.getCredentials().gameSessionId ?? '';
      const result = await this.d.api.placeBet({
        sessionId: sid,
        bets,
        gid: this.gid || sid,
      });

      const parsed = parseResultString(result.result);
      this.gid = result.nextRoundId || this.gid;

      const totalBet = this.d.state.bets.total;
      const betDetails = formatBetDetails(this.d.state.bets);
      const balanceBefore = this.d.state.balance;

      this.d.state.applyRoundResult({
        roundId: this.gid,
        nextRoundId: result.nextRoundId,
        playerCards: parsed.playerCards,
        bankerCards: parsed.bankerCards,
        playerScore: parsed.playerScore,
        bankerScore: parsed.bankerScore,
        winningBetIds: parsed.winningBetIds,
        result: parsed.gameResult,
        totalWinLose: result.totalWinLose,
        totalBet,
        positiveWinAmount: result.positiveWinAmount,
        hasPositiveWin: result.hasPositiveWin,
      });
      // Defer the balance update to the WIN/LOSE reveal (t=5 s) so the
      // displayed balance stays "bet deducted" through the card reveal and
      // only reflects winnings when the result is shown (req 2).
      this.pendingSettleBalance = result.afterBalance;

      GameLogManager.add({
        betAmount: totalBet,
        betDetails,
        winLoseAmount: result.totalWinLose,
        balanceBefore,
        balanceAfter: result.afterBalance,
        gameResult: formatGameResult(parsed.gameResult, parsed.playerScore, parsed.bankerScore),
        roundId: this.gid,
      });

      this.d.state.setPhase('cardReveal');
      this.d.rive.fireNoMoreBet();
      this.didShowNoMoreBet = !document.hidden;
      this.d.hud.setStatus('No more bets');

      // Safety-net: if the Rive event never fires, force-start reveal at 8 s
      this.scheduleTimer(() => {
        if (this.didStartCardReveal) return;
        this.didStartCardReveal = true;
        const round = this.d.state.currentRound;
        if (round) void this.playCardRevealSequence(round);
      }, 8000);
    } catch (e) {
      error('[Orchestrator] placeBet failed:', e);
      const msg = extractMessage(e);
      if (e instanceof ApiError && e.unauthorized) {
        this.d.state.setError('Session expired. Please reload.');
      } else {
        this.d.state.setError(msg);
      }
      this.d.hud.setStatus('ERROR: ' + msg);
    }
  }

  /**
   * Submit the locally-accumulated bets to the multiplayer hub. Invoked when
   * the bet countdown ends (and, as a fallback, on the server's EndBet) —
   * the multiplayer analog of single-player confirm().
   *
   * One PlaceBet is sent per bet-type carrying the accumulated amount; the
   * server adds each amount to the player's stake, so a single total per
   * type is equivalent to the original per-chip increments. Idempotent: the
   * didSubmitMpBets guard prevents a double-submit when both the countdown
   * tick and EndBet fire.
   */
  private submitMultiplayerBets(): void {
    const s = this.d.state;
    if (!s.isMultiplayer) return;
    if (this.didSubmitMpBets) return;
    this.didSubmitMpBets = true;
    // Lock betting locally so no further chips/clears slip in after submit.
    s.setMultiplayerCanBet(false);

    const lines = s.bets.toChipLines();
    debugLog(
      '[Orchestrator] submitMultiplayerBets — submitting',
      lines.length,
      'chip line(s):',
      lines,
    );
    if (lines.length === 0) return;
    for (const line of lines) {
      void this.d.hub.placeBet(line.betItemId, line.amount);
    }
  }

  // =================================================================
  // Phase / animation orchestration
  // =================================================================

  private startBettingRound(): void {
    this.cancelTimers();
    this.roundToken++;
    this.revealEpoch++;
    this.didStartCardReveal = false;
    this.didStartResultSequence = false;
    this.didShowWinLose = false;
    this.endingRevealDone = false;
    this.didResetAfterResult = false;
    this.pendingSettleBalance = null;
    this.didSettle = false;
    this.awaitingSettleBalance = false;
    this.didApplyResultSettlement = false;
    this.didSubmitMpBets = false;
    // New round hasn't cleared cards or shown the NoMoreBet popup yet. Note we
    // deliberately do NOT reset cardsVisible/lastClearCardType here: if the
    // previous round's t=8 clearCard collapsed into a hidden tab, the cards are
    // still drawn and resyncToCurrentState() needs lastClearCardType to flush
    // them on return.
    this.didClearCard = false;
    this.didShowNoMoreBet = false;
    this.didRecordHistory = false;

    this.d.state.resetForNewRound();
    this.d.chips.hardClear();
    this.d.state.setPhase('betting');

    const cfg = this.d.state.config;
    this.d.rive.setBetLimits(`$ ${cfg.minBet}`, `$ ${cfg.maxBet}`);
    this.d.rive.setBalanceText(formatCurrency(this.d.state.availableBalance));
    this.d.rive.setBetAmountText('0');

    this.d.rive.fireStartBet();
    this.d.rive.triggerGirlAnim(1);
    this.d.rive.setAllHistory(this.d.state.history);
    // If we fired into a hidden (frozen) SM the popup collapsed unseen — leave
    // the flag false so resync re-fires it on return.
    this.didShowStartBetPopup = !document.hidden;

    this.d.hud.setStatus('Place your bets');
  }

  /**
   * Inter-card delay (1 s, wall-clock). After waiting, reports whether this
   * reveal is still the current round. Returns false if the round was reset OR a
   * tab-return resync claimed ownership while we waited (revealEpoch changed) —
   * the caller then bails before firing the next card trigger, so a reveal that
   * ran across a tab-hide can't fight the resync snap or fire into a new round.
   */
  private async revealGap(epoch: number): Promise<boolean> {
    await wait(1000);
    return this.revealEpoch === epoch;
  }

  /**
   * Card reveal sequence. Mirrors _startCardRevealSequence (game_bloc.dart:1193-1228):
   *   P1 → B1 → P2 → B2 → (P3 if drawn) → (B3 if drawn)  with 1 s between steps,
   *   then 1 s, then _showResultAfterReveal.
   *
   * Card data lives on the *card* + *top* artboards. We push values first,
   * then call revealPlayerCard/revealBankerCard which fires CardPlayerBanker
   * with the right code (P1=1, B1=2, P2=4, B2=5, P3=3, B3=6).
   */
  private async playCardRevealSequence(round: {
    playerCards: number[];
    bankerCards: number[];
    playerScore: number;
    bankerScore: number;
    result?: GameResult;
  }): Promise<void> {
    // Record this round in the bead-plate history (HistoryPanel). This is the
    // single chokepoint every reveal funnels through — single-player (Rive
    // noMoreBet_End or the 8 s safety net) and multiplayer (StartPlay) — and
    // it's reached exactly once per round, guarded upstream by
    // didStartCardReveal, so the bead plate gets exactly one entry per round.
    //
    // Prefer the result captured on `round` over live state: the multiplayer
    // path schedules this behind a 1 s wait, and a next-round StartGame landing
    // in that gap would null out currentRound (resetForNewRound) and silently
    // drop the entry. The passed-in result is immune to that race.
    const result = round.result ?? this.d.state.currentRound?.result;
    if (result && !this.didRecordHistory) {
      this.didRecordHistory = true;
      MatchHistoryManager.add({
        result,
        playerPair: isPair(round.playerCards),
        bankerPair: isPair(round.bankerCards),
      });
    }

    // Token for this reveal — if the round restarts (tab-hide pauses a gap, a
    // new StartGame/startBettingRound lands) revealGap() bails so we never fire
    // card triggers into the next round.
    const epoch = this.revealEpoch;

    this.d.rive.setCards(round.playerCards, round.bankerCards);
    // Cards are now logically on the table (whether or not the tab is visible to
    // animate the flips). Stays true until clearCard runs while visible.
    this.cardsVisible = true;

    const playerCount = cardCount(round.playerCards);
    const bankerCount = cardCount(round.bankerCards);

    // P1
    this.d.rive.revealPlayerCard(1);
    if (!(await this.revealGap(epoch))) return;
    // B1
    this.d.rive.revealBankerCard(1);
    if (!(await this.revealGap(epoch))) return;
    // P2
    this.d.rive.revealPlayerCard(2);
    if (!(await this.revealGap(epoch))) return;
    // B2
    this.d.rive.revealBankerCard(2);
    if (!(await this.revealGap(epoch))) return;
    // P3 if drawn
    if (playerCount >= 3) {
      this.d.rive.revealPlayerCard(3);
      if (!(await this.revealGap(epoch))) return;
    }
    // B3 if drawn
    if (bankerCount >= 3) {
      this.d.rive.revealBankerCard(3);
      if (!(await this.revealGap(epoch))) return;
    }

    // Show scores + winner banner (game_bloc.dart:1231-1258 — _showResultAfterReveal)
    this.d.rive.setScores(round.playerScore, round.bankerScore);
    const winner = round.result ?? this.d.state.currentRound?.result;
    if (winner) this.d.rive.triggerWinner(winner);

    // 3 s after winner banner → result sequence.
    this.scheduleTimer(() => {
      if (this.didStartResultSequence) return;
      this.d.rive.triggerGirlAnim(2);
      this.startResultSequence();
    }, 3000);
  }

  /**
   * Result sequence. Mirrors _onStartResultSequence (game_bloc.dart:1262-1346):
   *   t=1 s — update balance text (we do this via state-change subscription)
   *   t=2 s — chip-fly-out by winMode + girlAnim(2)
   *   t=5 s — show win/lose popup (single-player)
   *   t=8 s — clearCard(getClearCardType(playerCount, bankerCount))
   *   t=13 s — startNewRound
   */
  private startResultSequence(): void {
    if (this.didStartResultSequence) return;
    // Read the round FIRST and bail BEFORE latching the guard: if the round data
    // isn't ready yet, latching here would permanently wedge the sequence (the
    // t=5 s settle timer would never be scheduled, so the deferred balance settle
    // would never fire). Leaving the guard unset lets a later trigger start it
    // once currentRound exists.
    const round = this.d.state.currentRound;
    if (!round) return;
    this.didStartResultSequence = true;
    this.cancelTimers();

    // t=2 s — chip animation
    this.scheduleTimer(() => {
      const winLose = round.totalWinLose;
      if (winLose > 0) {
        const winningBets = this.placedWinningBets(round.winningBetIds);
        this.d.chips.animateWinningChips(winningBets);
      } else if (winLose < 0) {
        this.d.chips.animateLosingChips();
      } else {
        this.d.chips.animateTie();
      }
      this.d.state.setPhase('showResult');
    }, 2000);

    // t=5 s — settle the balance, then reveal the YOU WIN / YOU LOSE popup.
    this.scheduleTimer(() => {
      // Reflect the post-round balance now that the result is shown (req 2).
      // Clears the resolved bets in the same commit so the available balance
      // lands exactly on the server's settled value.
      this.applySettlement();
      // "YOU WIN" with the GROSS winnings whenever the player won ANY bet area
      // (even on a net loss), else the net loss, else 0 (push / no bet → no
      // popup). Read fresh from currentRound — in multiplayer the Result event
      // replaces currentRound (patching in the real figures) and may land AFTER
      // this sequence captured `round`; falls back to the captured round.
      const displayWinLose = this.displayWinLoseFor(
        this.d.state.currentRound ?? round,
      );
      if (displayWinLose !== 0) {
        this.d.hud.setStatus(
          displayWinLose > 0
            ? `YOU WIN ${formatCurrency(displayWinLose)}`
            : `YOU LOSE ${formatCurrency(-displayWinLose)}`,
        );
        this.d.state.setPhase('result');
      }
      // The reveal point is reached now — flag it so a late multiplayer Result
      // can drive the popup as a backstop (see handleResult). On a push the
      // guard inside revealWinLosePanel stays unset, so a later real figure can
      // still reveal it.
      this.endingRevealDone = true;
      this.revealWinLosePanel(displayWinLose);
    }, 5000);

    // t=8 s — clearCard with the correct type (1/2/3/4 based on # cards drawn)
    this.scheduleTimer(() => {
      const playerCount = cardCount(round.playerCards);
      const bankerCount = cardCount(round.bankerCards);
      const type = clearCardType(playerCount, bankerCount);
      this.lastClearCardType = type;
      this.didClearCard = true;
      this.d.rive.clearCard(type);
      // If the tab is visible the clear animation actually plays, so the cards
      // really leave. If hidden, the trigger collapses (cards stay drawn) and
      // resyncToCurrentState() re-issues the clear on the live SM on return.
      if (!document.hidden) this.cardsVisible = false;
    }, 8000);

    // t=13 s — startNewRound (single-player). In multiplayer the hub's
    // StartGame event drives the round reset, not our local timer.
    this.scheduleTimer(() => this.resetToBettingIfNeeded(), 13000);
  }

  /**
   * The win/lose figure to DISPLAY for a round — mirrors the Flutter game's
   * displayWinLose: the GROSS winnings when the player won any bet area (even if
   * the net is a loss, so it still reads "YOU WIN"), otherwise the net loss,
   * otherwise 0 (no bet / push). Positive = win, negative = loss, 0 = neither.
   *
   * (Single-player fills positiveWinAmount in confirm() from parseBetResult;
   * multiplayer fills it in handleResult from the settlement Result rows.)
   */
  private displayWinLoseFor(round: GameRound | null): number {
    if (!round) return 0;
    if (round.positiveWinAmount > 0) return round.positiveWinAmount;
    return round.totalWinLose < 0 ? round.totalWinLose : 0;
  }

  /**
   * Show the YOU WIN / YOU LOSE popup (MainWinLose overlay) exactly once per
   * round. `display` follows displayWinLoseFor: positive = win (the gross
   * winnings), negative = lose (the net loss), 0 = nothing (push / no bet → no
   * popup; the guard is left unset so a later caller with the real figure can
   * still reveal it). Idempotent via didShowWinLose.
   *
   * The caller passes `display` EXPLICITLY rather than this reading currentRound,
   * because in multiplayer the authoritative settlement Result can arrive after
   * the reveal ran (with currentRound still at 0, or already null after
   * teardown), so the popup must be driven from the amount extracted in
   * handleResult.
   */
  private revealWinLosePanel(display: number): void {
    if (this.didShowWinLose) return;
    if (display === 0) return;
    // Don't fire the popup into a frozen (hidden-tab) SM — the one-shot trigger
    // would collapse unseen and we'd wrongly mark it shown. Leave didShowWinLose
    // unset so resyncToCurrentState() shows it the moment the tab returns.
    if (document.hidden) return;
    this.didShowWinLose = true;
    this.d.rive.showWinLose(display > 0, formatCurrency(Math.abs(display)));
  }

  /**
   * Apply the deferred balance settlement: set the wallet to the server's
   * post-round value and clear the resolved bets (atomically, to avoid a
   * one-frame flash of an intermediate available balance). Idempotent — safe
   * to call from the t=5 s timer and again from the teardown safety nets. If
   * the server balance isn't known yet (a late multiplayer Result), holds the
   * bets in place; handleResult applies the balance the moment it arrives.
   */
  private applySettlement(): void {
    if (this.didSettle) return;
    if (this.pendingSettleBalance != null) {
      // Server's post-round balance is known — finalize atomically.
      this.didSettle = true;
      this.d.state.settle(this.pendingSettleBalance);
    } else {
      // Multiplayer: the reveal reached the settle point but the server's
      // Result event (carrying the post-round balance) hasn't arrived yet.
      // Do NOT clear the bets here — the displayed balance is
      // `wallet − pending bets`, so clearing the bets while the wallet still
      // holds the pre-settlement value would make the balance jump UP to the
      // full (pre-bet) wallet for a moment (the "balance flashes to the old
      // value" bug). Instead, leave the bets so the display stays
      // bet-deducted, and let handleResult finalize atomically the instant the
      // balance arrives.
      this.awaitingSettleBalance = true;
    }
  }

  /**
   * Tear-down between rounds. Mirrors _onStartNewRound (game_bloc.dart:1520-1552).
   *
   * Idempotent — guarded by didResetAfterResult.
   */
  private resetToBettingIfNeeded(): void {
    if (this.didResetAfterResult) return;
    const p = this.d.state.phase;
    if (p !== 'result' && p !== 'showResult' && p !== 'cardReveal') return;
    this.didResetAfterResult = true;

    // Safety net: ensure the balance was settled even if the t=5 s timer was
    // skipped (e.g. a Rive evtYouWinEnd fired early).
    this.applySettlement();

    this.cancelTimers();

    // Update history + clear chips + start the next round.
    this.d.rive.setAllHistory(this.d.state.history);
    this.d.chips.hardClear();
    if (!this.d.state.isMultiplayer) this.startBettingRound();
  }

  // =================================================================
  // State-change → output sync
  // =================================================================

  private onStateChange(next: GameStateSnapshot, prev: GameStateSnapshot): void {
    if (next.balance !== prev.balance) {
      this.renderBalance();
    }

    if (next.bets !== prev.bets) {
      this.d.hud.setBetTotal(next.bets.total);
      this.d.rive.setBetAmountText(formatCurrency(next.bets.total));
      // Placing or clearing a bet changes the available balance — reflect
      // it immediately (req: balance updates the instant a bet is placed).
      this.renderBalance();
      this.d.controls.setControlsEnabled(this.canInteract(next), !next.bets.isEmpty);
    }

    if (next.selectedChipIndex !== prev.selectedChipIndex) {
      this.d.controls.setSelectedChip(next.selectedChipIndex);
    }

    if (
      next.phase !== prev.phase ||
      next.multiplayerCanBet !== prev.multiplayerCanBet
    ) {
      this.d.controls.setControlsEnabled(this.canInteract(next), !next.bets.isEmpty);
    }

    if (next.config !== prev.config) {
      this.d.controls.bindChips(next.config);
      this.d.controls.setSelectedChip(next.selectedChipIndex);
    }

    if (next.history !== prev.history) {
      this.d.rive.setAllHistory(next.history);
    }

    if (
      next.multiplayerBetSeconds !== prev.multiplayerBetSeconds ||
      next.multiplayerRestartSeconds !== prev.multiplayerRestartSeconds
    ) {
      if (next.multiplayerBetSeconds != null) {
        this.d.timer.showBetTimer(next.multiplayerBetSeconds);
      } else if (next.multiplayerRestartSeconds != null) {
        this.d.timer.showRestartTimer(next.multiplayerRestartSeconds);
      } else {
        this.d.timer.showBetTimer(null);
      }
    }
  }

  /** Push the available balance (wallet − pending bets) to HUD + Rive. */
  private renderBalance(): void {
    const avail = this.d.state.availableBalance;
    this.d.hud.setBalance(avail);
    this.d.rive.setBalanceText(formatCurrency(avail));
  }

  /**
   * Whether chip / play / clear controls should be live. Single-player: the
   * betting phase. Multiplayer: the hub's StartBet/EndBet and our
   * countdown-end submit gate it via `multiplayerCanBet`.
   */
  private canInteract(s: GameStateSnapshot): boolean {
    return s.isMultiplayer ? s.multiplayerCanBet : s.phase === 'betting';
  }

  private normalizeEnvelope(payload: unknown): Envelope {
    if (payload == null) return null;
    if (Array.isArray(payload)) {
      for (const item of payload) {
        if (item && typeof item === 'object' && !Array.isArray(item)) {
          const status =
            typeof payload[0] === 'string' ? (payload[0] as string) : null;
          if (status) {
            return { _status: status, ...(item as Record<string, unknown>) };
          }
          return item as Record<string, unknown>;
        }
      }
      return null;
    }
    if (typeof payload === 'object') return payload as Envelope;
    return null;
  }

  // =================================================================
  // Multiplayer
  // =================================================================

  private async startMultiplayer(): Promise<void> {
    this.d.state.setMultiplayerActive(true);
    this.d.state.setPhase('initializing');
    this.d.hud.setStatus('Connecting hub…');

    try {
      await this.d.hub.connect();

      // JoinGame's sessionId is the URL-DECODED bearer token, NOT the gsid
      // (playbook §8). We store the encoded form for REST Bearer; decode here.
      const encodedToken = this.d.api.getCredentials().sessionToken ?? '';
      const sessionId = encodedToken.includes('%')
        ? decodeURIComponent(encodedToken)
        : encodedToken;
      if (!sessionId) throw new Error('Missing session token for JoinGame');

      debugLog('[Orchestrator] joining hub with decoded token, gameKey=', GAME_KEY_MULTI);
      await this.d.hub.joinGame(sessionId, GAME_KEY_MULTI);
      await this.d.hub.getMessages();
      this.d.hud.setStatus('Waiting for next round…');
    } catch (e) {
      error('[Orchestrator] multiplayer connect failed:', e);
      this.d.state.setError(extractMessage(e));
      this.d.state.setMultiplayerActive(false);
    }
  }

  private bindHubEvents(): void {
    this.d.hub.on((event) => this.handleHubEvent(event));
  }

  private handleHubEvent(event: HubEvent): void {
    const env = this.normalizeEnvelope(event.payload);

    // Index-based dedup (playbook §8 item 19).
    if (env && typeof env.Index === 'number') {
      const idx = env.Index;
      if (this.lastMpEventIndex != null && idx <= this.lastMpEventIndex) return;
      this.lastMpEventIndex = idx;
    }
    // Server's Index counter resets to 1 every round — clear our tracker
    // when we see State='restart'.
    if (env && String(env.State ?? '').toLowerCase() === 'restart') {
      this.lastMpEventIndex = null;
    }

    switch (event.name) {
      case '_connection':
        debugLog('[Hub] connection state', event.payload);
        break;
      case 'PlayerJoined': this.handlePlayerJoined(env); break;
      case 'PlayerLeft':   this.handlePlayerLeft(env); break;
      case 'StartGame':    this.handleStartGame(env); break;
      case 'StartBet':     this.handleStartBet(env); break;
      case 'EndBet':       this.handleEndBet(env); break;
      case 'StartPlay':    this.handleStartPlay(env); break;
      case 'Result':       this.handleResult(env); break;
      case 'PlaceBet':     this.handlePlaceBetAck(env); break;
      case 'GetMessages':  break; // replay — individual handlers fire
      default:
        debugLog('[Hub] unhandled event', event.name, env);
    }
  }

  private handlePlayerJoined(env: Envelope): void {
    if (env?._status === 'Fail') {
      const msg = String(env.Content ?? 'Failed to join multiplayer table');
      error('[Hub] PlayerJoined Fail:', msg);
      this.d.state.setError(msg);
      this.d.hud.setStatus('ERROR: ' + msg);
      this.stopMultiplayer();
      this.d.state.setPhase('modeSelection');
      return;
    }
    const game = (env?.Data && typeof env.Data === 'object'
      ? (env.Data as { Game?: unknown }).Game
      : undefined) as Record<string, unknown> | undefined;
    if (game) {
      const chips = parseChipsField(game.Chips ?? game.chips);
      if (chips.length > 0) {
        this.d.state.setConfig({ ...this.d.state.config, chipValues: chips });
      }
    }
  }

  private handlePlayerLeft(env: Envelope): void {
    const msg = String(env?.Content ?? 'You have been disconnected.');
    this.d.state.setPlayerKickedMessage(msg);
    this.stopMultiplayer();
    this.d.state.setPhase('modeSelection');
    this.d.hud.setStatus('Session ended — please reload');
  }

  private handleStartGame(_env: Envelope): void {
    this.cancelBetCountdown();
    this.cancelRestartCountdown();
    this.cancelTimers();
    this.roundToken++;
    this.revealEpoch++;

    this.didStartCardReveal = false;
    this.didStartResultSequence = false;
    this.didShowWinLose = false;
    this.endingRevealDone = false;
    this.didResetAfterResult = false;
    this.pendingSettleBalance = null;
    this.didSettle = false;
    this.awaitingSettleBalance = false;
    this.didApplyResultSettlement = false;
    this.didSubmitMpBets = false;
    // See startBettingRound: keep cardsVisible/lastClearCardType so resync can
    // flush a prior round's cards if its clearCard collapsed into a hidden tab.
    this.didClearCard = false;
    this.didShowStartBetPopup = false;
    this.didShowNoMoreBet = false;
    this.didRecordHistory = false;

    this.d.state.setMultiplayerCanBet(false);
    this.d.state.setMultiplayerBetSeconds(null);
    this.d.state.setMultiplayerRestartSeconds(null);
    this.d.state.resetForNewRound();
    this.d.chips.hardClear();
    this.d.state.setPhase('newRound');
    this.d.rive.setAllHistory(this.d.state.history);
  }

  private handleStartBet(env: Envelope): void {
    const seconds = secondsBetween(env?.StartTime, env?.EndTime);
    debugLog('[Hub] StartBet — countdown', seconds, 's');
    // Fresh bet window — allow a new batched submission this round.
    this.didSubmitMpBets = false;
    this.d.state.setPhase('betting');
    this.d.state.setMultiplayerCanBet(true);
    // Apply the server-configured min/max bet text. Single player does this in
    // startBettingRound(), but multiplayer never calls that (the hub drives the
    // rounds), so without this the min/max display stays blank/stale. Uses the
    // getInfo-loaded config — mirrors amg_dragontiger_pixi handleStartBet.
    const cfg = this.d.state.config;
    this.d.rive.setBetLimits(`$ ${cfg.minBet}`, `$ ${cfg.maxBet}`);
    this.d.rive.fireStartBet();
    this.d.rive.triggerGirlAnim(1);
    this.didShowStartBetPopup = !document.hidden;
    this.d.hud.setStatus('Place your bets');
    // End the visible countdown BET_SUBMIT_BUFFER_SEC seconds early so the
    // batched submit (at countdown 0) reaches the backend before the real
    // window closes and play starts.
    this.startBetCountdown(Math.max(0, seconds - BET_SUBMIT_BUFFER_SEC));
  }

  private handleEndBet(_env: Envelope): void {
    this.cancelBetCountdown();
    // Submit now if the local countdown didn't already (server closed the
    // window first). Idempotent via didSubmitMpBets.
    this.submitMultiplayerBets();
    this.d.state.setMultiplayerCanBet(false);
    this.d.state.setMultiplayerBetSeconds(null);
    this.d.state.setPhase('bettingClosed');
    this.d.rive.fireNoMoreBet();
    this.didShowNoMoreBet = !document.hidden;
    this.d.hud.setStatus('No more bets');
  }

  private handleStartPlay(env: Envelope): void {
    const raw = String(env?.Result ?? '');
    if (!raw) return;
    let parsed;
    try {
      parsed = parseResultString(raw);
    } catch (e) {
      warn('[Hub] StartPlay parse failed:', e, raw);
      return;
    }
    this.d.state.setMultiplayerResultString(raw);
    this.d.state.applyRoundResult({
      roundId: '',
      nextRoundId: '',
      playerCards: parsed.playerCards,
      bankerCards: parsed.bankerCards,
      playerScore: parsed.playerScore,
      bankerScore: parsed.bankerScore,
      winningBetIds: parsed.winningBetIds,
      result: parsed.gameResult,
      totalWinLose: 0,
      totalBet: this.d.state.bets.total,
      positiveWinAmount: 0,
      hasPositiveWin: false,
    });

    if (!this.didStartCardReveal) {
      this.didStartCardReveal = true;
      this.d.state.setPhase('cardReveal');
      const revealRound = {
        playerCards: parsed.playerCards,
        bankerCards: parsed.bankerCards,
        playerScore: parsed.playerScore,
        bankerScore: parsed.bankerScore,
        // Captured here so the bead-plate record + winner banner survive a
        // next-round StartGame nulling currentRound during the 1 s wait below.
        result: parsed.gameResult,
      };
      // 1 s of breathing room between the result (StartPlay) arriving and the
      // first card flip. Single player gets this gap from the NoMoreBet popup
      // animation that precedes the reveal; multiplayer snaps StartPlay straight
      // into the reveal, so add it explicitly. Uses a plain wait (like the
      // reveal's own inter-card delays) so it isn't cancelled by the result
      // sequence's cancelTimers(). Bails if a new round started (or a tab-return
      // resync claimed ownership) during the gap.
      const epoch = this.revealEpoch;
      void (async () => {
        await wait(1000);
        if (this.revealEpoch !== epoch) return;
        await this.playCardRevealSequence(revealRound);
      })();
    }
  }

  private handleResult(env: Envelope): void {
    const state = String(env?.State ?? '').toLowerCase();
    if (state === 'restart' || env?.StartTime || env?.EndTime) {
      const seconds = secondsBetween(env?.StartTime, env?.EndTime);
      if (seconds > 0) {
        debugLog('[Hub] Result/restart — restart countdown', seconds, 's');
        this.d.state.setMultiplayerGameState('restart');
        this.startRestartCountdown(seconds);
        return;
      }
    }

    // Start the visual result sequence right away — it's purely cosmetic (chips,
    // winner banner) and must NOT wait on the balance round-trip below.
    // Idempotent: Rive's evtResultStop / the reveal tail may have started it.
    if (!this.didStartResultSequence) this.startResultSequence();

    // Settle off the AUTHORITATIVE post-round balance. The backend stopped
    // returning a usable per-bet figure (WinLost is 0 even on a loss) and the
    // Result event no longer carries a per-user Balance, so the wallet can't be
    // derived locally — we read it from the server (/action/balance). Async.
    void this.settleFromResult(env);
  }

  /**
   * Resolve the player's authoritative post-round balance, then run settlement.
   *
   * Balance source, in order of preference:
   *   1. The Result event's per-user `Balance` field — used when the server
   *      still sends it.
   *   2. /action/balance (checkBalance) — the backend now omits Balance from the
   *      Result event AND returns WinLost=0 even on a loss, so neither the wallet
   *      nor the net P&L can be derived locally; the only correct source is the
   *      server's own balance. NOTE: /action/getinfo does NOT carry a balance
   *      (config only), so we must use /action/balance.
   *
   * Async because of the balance round-trip, so it captures the round token up
   * front and bails if a new round started before the balance arrived — a slow
   * fetch must never settle a finished round into the next round's wallet.
   */
  private async settleFromResult(env: Envelope): Promise<void> {
    const token = this.roundToken;
    const userName = (this.d.api.getCredentials().userName || '').toLowerCase();
    const winLose = extractWinLoseForUser(env?.Data, userName);
    // Gross winnings from the user's WINNING bet rows (not the net). Drives the
    // "YOU WIN" popup whenever any area won, even if `winLose` (net) is negative.
    const positiveWin = extractPositiveWinForUser(env?.Data, userName);
    let balance = extractBalanceForUser(env?.Data, userName);
    if (balance == null) {
      // Server didn't include the post-round balance — fetch it from
      // /action/balance.
      balance = await this.fetchLatestBalance();
    }

    if (token !== this.roundToken) {
      debugLog('[Hub] Result settlement dropped — round advanced during balance fetch');
      return;
    }

    this.applyResultSettlement(winLose, positiveWin, balance);
  }

  /**
   * Fetch the latest wallet balance from the backend via /action/balance
   * (checkBalance) — the same endpoint the startup balance fetch uses.
   * (/action/getinfo carries config only, no balance.) Returns null on failure
   * so the caller can fall back to the locally-tracked wallet rather than
   * zeroing the balance on a transient network error.
   */
  private async fetchLatestBalance(): Promise<number | null> {
    try {
      const sessionId = this.d.api.getCredentials().gameSessionId ?? '';
      const balance = await this.d.api.checkBalance({ sessionId });
      debugLog('[Hub] /action/balance post-result balance =', balance);
      return balance;
    } catch (e) {
      warn('[Hub] checkBalance after result failed — keeping local balance:', e);
      return null;
    }
  }

  /**
   * Apply the resolved post-round balance: settle the wallet, drive the YOU WIN /
   * YOU LOSE popup, and log the round. `balance` is the authoritative post-round
   * wallet (from the Result event or /action/balance); null only when both
   * sources failed, in which case we fall back to `balanceBefore + winLose`.
   */
  private applyResultSettlement(
    winLose: number,
    positiveWin: number,
    balance: number | null,
  ): void {
    // Once per round — a retransmitted settlement Result must not re-run this
    // (it would read the already-settled wallet as balanceBefore and double-log).
    if (this.didApplyResultSettlement) return;
    this.didApplyResultSettlement = true;

    const totalBet = this.d.state.bets.total;
    const betDetails = formatBetDetails(this.d.state.bets);
    const balanceBefore = this.d.state.balance;
    const balanceAfter = balance ?? balanceBefore + winLose;

    this.d.state.setMultiplayerWinLose(winLose);
    // Defer the balance update to the WIN/LOSE reveal (t=5 s), like
    // single-player (req 2).
    this.pendingSettleBalance = balanceAfter;
    if (this.awaitingSettleBalance && !this.didSettle) {
      // The reveal already reached the settle point and was only waiting on
      // this balance — finalize atomically now (balance update + bet clear),
      // avoiding an early bet-clear that would flash the pre-settlement
      // wallet.
      this.didSettle = true;
      this.awaitingSettleBalance = false;
      this.d.state.settle(balanceAfter);
    } else if (this.didSettle) {
      // Late Result after settlement already applied — don't lose the
      // server's authoritative balance.
      this.d.state.setBalance(balanceAfter);
    }

    const cur = this.d.state.currentRound;
    if (cur) {
      this.d.state.applyRoundResult({
        ...cur,
        totalWinLose: winLose,
        positiveWinAmount: positiveWin,
        hasPositiveWin: positiveWin > 0,
      });
      GameLogManager.add({
        betAmount: totalBet,
        betDetails,
        winLoseAmount: winLose,
        balanceBefore,
        balanceAfter,
        gameResult: formatGameResult(cur.result, cur.playerScore, cur.bankerScore),
        roundId: cur.roundId || null,
      });
    }

    // Backstop: drive the YOU WIN / YOU LOSE popup from the AUTHORITATIVE
    // settlement figures when the Result lands AFTER the t=5 s reveal already
    // ran with a still-zero amount (the reveal reads currentRound, which stays 0
    // until this event arrives — and may even be null after teardown). "YOU WIN"
    // (gross winnings) whenever any area won, else "YOU LOSE" (net loss), else
    // nothing. Only once the reveal point is past (or the round was already torn
    // down) so it never appears mid-reveal; if Result arrived BEFORE the reveal,
    // currentRound was patched above and the t=5 s reveal shows it instead.
    // Idempotent via didShowWinLose.
    const resultDisplay = positiveWin > 0 ? positiveWin : winLose < 0 ? winLose : 0;
    if (this.endingRevealDone || this.didResetAfterResult) {
      this.revealWinLosePanel(resultDisplay);
    }
  }

  /**
   * PlaceBet ack — server confirmed (part of) our batched bet submission.
   *
   * No-op for balance: bets are submitted as a single batch when the
   * countdown ends and the displayed balance is optimistic (wallet −
   * pending bets). The authoritative wallet is re-synced from the Result
   * event at settlement, so applying the ack's per-bet balance here would
   * double-count the deduction.
   */
  private handlePlaceBetAck(_env: Envelope): void {
    // intentionally empty (see above)
  }

  // -----------------------------------------------------------------
  // Countdown timers
  // -----------------------------------------------------------------

  private startBetCountdown(seconds: number): void {
    this.cancelBetCountdown();
    let remaining = Math.max(0, Math.floor(seconds));
    this.d.state.setMultiplayerBetSeconds(remaining);
    this.betCountdownHandle = window.setInterval(() => {
      remaining -= 1;
      if (remaining <= 0) {
        this.cancelBetCountdown();
        this.d.state.setMultiplayerBetSeconds(null);
        // Countdown ended — submit the batched bets now (req 3: multiplayer
        // submits when the countdown ends, not per-chip).
        this.submitMultiplayerBets();
      } else {
        this.d.state.setMultiplayerBetSeconds(remaining);
      }
    }, 1000);
  }

  private cancelBetCountdown(): void {
    if (this.betCountdownHandle != null) {
      window.clearInterval(this.betCountdownHandle);
      this.betCountdownHandle = null;
    }
  }

  private startRestartCountdown(seconds: number): void {
    this.cancelRestartCountdown();
    let remaining = Math.max(0, Math.floor(seconds));
    this.d.state.setMultiplayerRestartSeconds(remaining);
    this.restartCountdownHandle = window.setInterval(() => {
      remaining -= 1;
      if (remaining <= 0) {
        this.cancelRestartCountdown();
        this.d.state.setMultiplayerRestartSeconds(null);
      } else {
        this.d.state.setMultiplayerRestartSeconds(remaining);
      }
    }, 1000);
  }

  private cancelRestartCountdown(): void {
    if (this.restartCountdownHandle != null) {
      window.clearInterval(this.restartCountdownHandle);
      this.restartCountdownHandle = null;
    }
  }

  private stopMultiplayer(): void {
    this.cancelBetCountdown();
    this.cancelRestartCountdown();
    void this.d.hub.disconnect();
    this.d.state.setMultiplayerActive(false);
  }

  // =================================================================
  // Tab-visibility resync ("stay live, snap on return")
  // =================================================================

  /**
   * Snap Rive visuals to the current authoritative state when the tab becomes
   * visible again. Wired to `document.visibilitychange` in main.ts.
   *
   * Browsers freeze Rive's rAF while a tab is hidden, so any trigger the round
   * timeline fired meanwhile collapsed (cards stuck, popups missed, the SM
   * possibly stranded). The game STATE is always current though — server events
   * and the wall-clock timers updated phase/currentRound/balance/history without
   * needing rAF — so on return we just re-drive Rive from that state.
   *
   * resync issues NO timeline timers and cancels nothing: the wall-clock
   * sequence (t=2/5/8/13 s, the placeBet promise, single-player startBettingRound)
   * keeps running so rounds still complete. Card-reveal flips are one-shot
   * triggers that collapse if fired together in one frame, so the reveal snap is
   * micro-staggered one trigger per rAF frame (each gets its own advance()).
   */
  resyncToCurrentState(): void {
    const s = this.d.state;
    if (!s.isRiveReady) return;
    const phase = s.phase;

    // Persistent text/history inputs are always safe to re-assert (last-write
    // numbers, never one-shot), so push them for every live phase.
    this.renderBalance();
    this.d.rive.setBetAmountText(formatCurrency(s.bets.total));
    const cfg = s.config;
    this.d.rive.setBetLimits(`$ ${cfg.minBet}`, `$ ${cfg.maxBet}`);
    this.d.rive.setAllHistory(s.history);

    switch (phase) {
      case 'betting':
      case 'newRound': {
        // Flush a prior round's cards if its t=8 clearCard collapsed while hidden.
        if (this.cardsVisible) this.flushStaleCards();
        // Re-show the "place your bets" popup only if it was lost to a frozen SM.
        // Set the flag synchronously so a second rapid resync can't double-fire.
        if (!this.didShowStartBetPopup) {
          this.didShowStartBetPopup = true;
          this.doubleRaf(() => {
            this.d.rive.fireStartBet();
            this.d.rive.triggerGirlAnim(1);
          });
        }
        return;
      }
      case 'bettingClosed': {
        if (this.cardsVisible) this.flushStaleCards();
        if (!this.didShowNoMoreBet) {
          this.didShowNoMoreBet = true;
          this.doubleRaf(() => this.d.rive.fireNoMoreBet());
        }
        return;
      }
      case 'waitingResult': {
        // Single-player network round-trip: confirm()'s success path fires the
        // NoMoreBet popup when the result arrives, so don't pre-fire it here.
        // Just flush any stale prior-round cards (persistent inputs already set).
        if (this.cardsVisible) this.flushStaleCards();
        return;
      }
      case 'cardReveal':
      case 'showResult':
      case 'result':
        this.snapBoard(phase);
        return;
      default:
        // idle / initializing / modeSelection / error — nothing to repair.
        return;
    }
  }

  /**
   * Re-issue the cards-back-to-deck animation on the live SM when a prior
   * clearCard collapsed into a hidden tab (cards still drawn on return).
   */
  private flushStaleCards(): void {
    const type = this.lastClearCardType ?? 4;
    this.cardsVisible = false;
    // Defer the one-shot clear trigger past the rAF warmup so it lands after
    // Rive re-arms its render loop on tab-return (same reason microStagger warms up).
    this.doubleRaf(() => this.d.rive.clearCard(type));
  }

  /**
   * Snap the card table to the current round on tab-return. Claims ownership of
   * any in-flight reveal (bumps revealEpoch so its revealGap bails — preventing a
   * double winner banner), then either flushes the cards (if the t=8 clearCard
   * already passed) or re-deals them fully flipped via a micro-staggered trigger
   * loop. For single-player it re-creates the reveal→result handoff the orphaned
   * sequence would have scheduled, so the round still progresses.
   */
  private snapBoard(phase: GamePhase): void {
    const round = this.d.state.currentRound;
    if (!round) return;

    // Claim ownership so any in-flight playCardRevealSequence bails at its next
    // revealGap and can't also fire reveal/winner triggers.
    this.revealEpoch++;
    const epoch = this.revealEpoch;
    const token = ++this.resyncToken;

    // Record the round in the bead-plate history if the reveal that normally
    // does it was orphaned by this resync (the multiplayer 1 s pre-reveal wait).
    // Guarded so a round is logged exactly once.
    if (round.result && !this.didRecordHistory) {
      this.didRecordHistory = true;
      MatchHistoryManager.add({
        result: round.result,
        playerPair: isPair(round.playerCards),
        bankerPair: isPair(round.bankerCards),
      });
    }

    // Cards should already be gone (t=8 passed): flush instead of re-dealing.
    if (this.didClearCard) {
      this.flushStaleCards();
      if (phase === 'result') this.revealWinLosePanel(this.displayWinLoseFor(round));
      return;
    }

    // Re-deal the board fully flipped. setCards is a persistent number write;
    // the flip triggers are micro-staggered below so none collapse.
    this.d.rive.setCards(round.playerCards, round.bankerCards);
    this.cardsVisible = true;

    const steps: Array<() => void> = [
      () => this.d.rive.revealPlayerCard(1),
      () => this.d.rive.revealBankerCard(1),
      () => this.d.rive.revealPlayerCard(2),
      () => this.d.rive.revealBankerCard(2),
    ];
    if (cardCount(round.playerCards) >= 3) steps.push(() => this.d.rive.revealPlayerCard(3));
    if (cardCount(round.bankerCards) >= 3) steps.push(() => this.d.rive.revealBankerCard(3));
    steps.push(() => this.d.rive.setScores(round.playerScore, round.bankerScore));
    steps.push(() => {
      if (round.result) this.d.rive.triggerWinner(round.result);
    });
    steps.push(() => {
      // At/after the reveal point, re-show the win/lose popup (it was suppressed
      // while hidden so didShowWinLose is still unset; idempotent if not).
      if (phase === 'result') this.revealWinLosePanel(this.displayWinLoseFor(round));
      // Single-player: the orphaned reveal would have scheduled the result
      // sequence at its tail. Re-create that handoff or the round strands.
      // (Multiplayer's result sequence is driven by the hub's Result event.)
      if (this.d.state.isMultiplayer) return;
      if (this.didStartResultSequence) return;
      this.scheduleTimer(() => {
        if (this.didStartResultSequence) return;
        this.d.rive.triggerGirlAnim(2);
        this.startResultSequence();
      }, 3000);
    });

    this.microStagger(steps, token, epoch);
  }

  /**
   * Fire each step on its own rAF frame so one-shot Rive triggers each get a
   * fresh advance() and none collapse. A 2-frame warmup precedes the first step
   * so it never lands before Rive re-arms its render loop on tab-return. Aborts
   * if a newer resync started (token) or the round changed (epoch).
   */
  private microStagger(steps: Array<() => void>, token: number, epoch: number): void {
    let i = 0;
    const run = (): void => {
      if (token !== this.resyncToken || epoch !== this.revealEpoch) return;
      if (i >= steps.length) return;
      steps[i++]();
      requestAnimationFrame(run);
    };
    requestAnimationFrame(() => requestAnimationFrame(run));
  }

  /** Run `fn` after two animation frames (lets Rive re-arm its loop first). */
  private doubleRaf(fn: () => void): void {
    requestAnimationFrame(() => requestAnimationFrame(fn));
  }

  /**
   * The set of placed bet types that won. Baccarat's main bets settle by
   * winningBetIds (server's @-list); side bets may also be present in
   * the list when their pair/perfect-pair condition is met.
   */
  private placedWinningBets(winningIds: number[]): Set<BetType> {
    const winners = new Set<BetType>();
    for (const id of winningIds) {
      const b = betTypeFromId(id);
      if (b) winners.add(b);
    }
    const out = new Set<BetType>();
    for (const b of this.d.state.bets.bets) {
      if (winners.has(b)) out.add(b);
    }
    return out;
  }

  private scheduleTimer(fn: () => void, ms: number): void {
    // Wall-clock: the round timeline must keep running while the tab is hidden
    // so it never lags the server. Rive can't render a hidden tab, so visuals
    // are repaired on return by resyncToCurrentState(), not by pausing here.
    const id = window.setTimeout(fn, ms);
    this.sequenceTimers.push(id);
  }

  private cancelTimers(): void {
    for (const id of this.sequenceTimers) window.clearTimeout(id);
    this.sequenceTimers = [];
  }
}

// =====================================================================
// Helpers (file-scope)
// =====================================================================

function wait(ms: number): Promise<void> {
  return new Promise((r) => window.setTimeout(r, ms));
}

function formatCurrency(n: number): string {
  return Math.round(n).toLocaleString('en-US');
}

function ensureUrlEncoded(token: string): string {
  if (!token) return token;
  if (token.includes('%')) return token;
  return encodeURIComponent(token);
}

function parseGameUrl(
  currentGameUrl: string,
  loginToken: string,
): { gameToken: string; gsid: string } {
  if (!currentGameUrl) return { gameToken: loginToken, gsid: '' };
  try {
    const u = new URL(currentGameUrl, 'https://placeholder.invalid');
    const urlToken = u.searchParams.get('token');
    const urlGsid = u.searchParams.get('gsid');
    return {
      gameToken: urlToken && urlToken.length > 0 ? urlToken : loginToken,
      gsid: urlGsid ?? '',
    };
  } catch {
    return { gameToken: loginToken, gsid: '' };
  }
}

function formatGameResult(
  winner: GameResult,
  playerScore: number,
  bankerScore: number,
): string {
  const label =
    winner === 'player' ? 'Player' : winner === 'banker' ? 'Banker' : 'Tie';
  return `${label} (${playerScore} vs ${bankerScore})`;
}

const BET_LABEL: Record<BetType, string> = {
  player: 'Player',
  banker: 'Banker',
  tie: 'Tie',
  playerPair: 'P.Pair',
  bankerPair: 'B.Pair',
  perfectPair: 'P.Perfect',
  eitherPair: 'Either',
};

function formatBetDetails(bets: {
  bets: readonly BetType[];
  amountFor(b: BetType): number;
}): string {
  const parts: string[] = [];
  for (const b of bets.bets) {
    const amt = bets.amountFor(b);
    if (amt > 0) parts.push(`${BET_LABEL[b]}:${formatCurrency(amt)}`);
  }
  return parts.join(', ');
}

function extractMessage(e: unknown): string {
  if (e instanceof Error) return e.message;
  if (typeof e === 'string') return e;
  try { return JSON.stringify(e); } catch { return String(e); }
}

type Envelope = Record<string, unknown> | null;

function asNumber(v: unknown): number {
  if (typeof v === 'number') return v;
  if (typeof v === 'string') {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
}

function parseChipsField(raw: unknown): number[] {
  const out: number[] = [];
  if (typeof raw === 'string') {
    for (const piece of raw.split(',')) {
      const n = parseInt(piece.trim(), 10);
      if (Number.isFinite(n) && n > 0) out.push(n);
    }
  } else if (Array.isArray(raw)) {
    for (const v of raw) {
      const n = typeof v === 'number' ? v : parseInt(String(v), 10);
      if (Number.isFinite(n) && n > 0) out.push(n);
    }
  }
  return out;
}

function secondsBetween(start: unknown, end: unknown): number {
  if (typeof start !== 'string' || typeof end !== 'string') return 0;
  const a = Date.parse(start);
  const b = Date.parse(end);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return 0;
  return Math.round((b - a) / 1000);
}

/**
 * Sum the current user's WinLose values out of the Data array. See playbook §8.
 */
function extractWinLoseForUser(data: unknown, userNameLower: string): number {
  if (data == null || !userNameLower) return 0;
  if (Array.isArray(data)) {
    let sum = 0;
    for (const raw of data) {
      if (!raw || typeof raw !== 'object') continue;
      const item = raw as Record<string, unknown>;
      const u = pickUserName(item);
      if (!u || u !== userNameLower) continue;
      sum += asNumber(item.WinLost ?? item.WinLose ?? item.winLost ?? item.winLose);
    }
    return sum;
  }
  if (typeof data === 'object') {
    const m = data as Record<string, unknown>;
    const u = pickUserName(m);
    if (u && u !== userNameLower) return 0;
    return asNumber(m.TotalWinLose ?? m.WinLose ?? m.winLose ?? m.WinLost);
  }
  return 0;
}

/**
 * Sum ONLY the current user's POSITIVE WinLose rows out of the Data array — the
 * gross winnings from the bet areas that WON. Mirrors the Flutter game's
 * positiveForUser. Used so the YOU WIN popup shows whenever the player won any
 * area, even if their net result is a loss. Same row-matching rules as
 * extractWinLoseForUser (user rows only).
 */
function extractPositiveWinForUser(data: unknown, userNameLower: string): number {
  if (data == null || !userNameLower) return 0;
  if (Array.isArray(data)) {
    let sum = 0;
    for (const raw of data) {
      if (!raw || typeof raw !== 'object') continue;
      const item = raw as Record<string, unknown>;
      const u = pickUserName(item);
      if (!u || u !== userNameLower) continue;
      const v = asNumber(item.WinLost ?? item.WinLose ?? item.winLost ?? item.winLose);
      if (v > 0) sum += v;
    }
    return sum;
  }
  if (typeof data === 'object') {
    const m = data as Record<string, unknown>;
    const u = pickUserName(m);
    if (u && u !== userNameLower) return 0;
    const v = asNumber(m.TotalWinLose ?? m.WinLose ?? m.winLose ?? m.WinLost);
    return v > 0 ? v : 0;
  }
  return 0;
}

/** Extract the matched user's Balance — rules in playbook §8. */
function extractBalanceForUser(data: unknown, userNameLower: string): number | null {
  if (data == null || !userNameLower) return null;
  if (Array.isArray(data)) {
    for (const raw of data) {
      if (!raw || typeof raw !== 'object') continue;
      const item = raw as Record<string, unknown>;
      const u = pickUserName(item);
      if (!u || u !== userNameLower) continue;
      const b = item.Balance ?? item.balance;
      if (b == null) return null;
      const n = typeof b === 'number' ? b : Number(b);
      return Number.isFinite(n) ? n : null;
    }
    return null;
  }
  if (typeof data === 'object') {
    const m = data as Record<string, unknown>;
    const u = pickUserName(m);
    if (!u || u !== userNameLower) return null;
    const b = m.Balance ?? m.balance;
    if (b == null) return null;
    const n = typeof b === 'number' ? b : Number(b);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function pickUserName(item: Record<string, unknown>): string {
  const raw =
    item.Username ?? item.username ?? item.UserName ?? item.userName;
  return raw == null ? '' : String(raw).toLowerCase();
}
